import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import StructuredData from "@/components/feature/StructuredData";
import { movies } from "@/mocks/movies";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === Number(id));

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-foreground-400" />
            </div>
            <p className="text-foreground-500 mb-4">未找到该影视内容</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-primary-500 hover:text-primary-600 font-medium text-sm"
            >
              <i className="ri-arrow-left-line" /> 返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedMovies = movies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .slice(0, 6);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Movie",
    name: movie.title,
    description: movie.description,
    image: movie.poster,
    dateCreated: `${movie.year}`,
    director: {
      "@type": "Person",
      name: movie.director,
    },
    actor: movie.cast.map((c) => ({
      "@type": "Person",
      name: c,
    })),
    countryOfOrigin: {
      "@type": "Country",
      name: movie.region,
    },
  };

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: movie.categoryName, href: `/category/${movie.category}` },
    { label: movie.title },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <StructuredData webpageSchema={articleSchema} />
      <Navbar />

      <div className="pt-16">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <main className="w-full px-4 md:px-8 py-6 md:py-10">
        <article className="max-w-5xl mx-auto">
          {/* Movie Header */}
          <div className="flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="flex-shrink-0 w-full sm:w-[240px] md:w-[300px] mx-auto md:mx-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden">
                <img
                  src={movie.poster}
                  alt={movie.title}
                  title={`${movie.title} - 天美麻花星空影视免费观看`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {movie.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="inline-flex items-center gap-1 bg-primary-100 text-primary-700 text-sm font-bold px-2.5 py-1 rounded-md">
                  <i className="ri-star-fill text-xs" /> {movie.rating}
                </span>
                <span className="text-sm text-foreground-500">{movie.year}</span>
                <span className="text-sm text-foreground-400">|</span>
                <span className="text-sm text-foreground-500">{movie.region}</span>
                {movie.duration && (
                  <>
                    <span className="text-sm text-foreground-400">|</span>
                    <span className="text-sm text-foreground-500">{movie.duration}</span>
                  </>
                )}
                {movie.episodes && (
                  <>
                    <span className="text-sm text-foreground-400">|</span>
                    <span className="text-sm text-foreground-500">共{movie.episodes}集</span>
                  </>
                )}
                <span className="inline-block bg-accent-100 text-accent-700 text-xs font-medium px-2 py-0.5 rounded">
                  {movie.status}
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-5">
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                {movie.description}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-foreground-400">导演：</span>
                  <span className="text-foreground-700">{movie.director}</span>
                </div>
                <div>
                  <span className="text-foreground-400">主演：</span>
                  <span className="text-foreground-700">{movie.cast.join("、")}</span>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-play-fill text-lg" /> 立即播放
                </button>
                <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-100 text-foreground-700 text-sm font-medium rounded-full hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-heart-line" /> 收藏
                </button>
                <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-100 text-foreground-700 text-sm font-medium rounded-full hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-share-forward-line" /> 分享
                </button>
              </div>
            </div>
          </div>

          {/* Full Description */}
          <section className="mt-10">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">
              剧情简介
            </h2>
            <div className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">
              {movie.longDescription}
            </div>
          </section>

          {/* Episodes / Cast */}
          {movie.episodes && (
            <section className="mt-10">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">
                剧集列表
              </h2>
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                {Array.from({ length: Math.min(movie.episodes, 20) }, (_, i) => (
                  <button
                    key={i}
                    className="px-3 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-md hover:bg-primary-100 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    第{i + 1}集
                  </button>
                ))}
                {movie.episodes > 20 && (
                  <button className="px-3 py-2 text-xs font-medium text-foreground-400 bg-background-100 rounded-md cursor-pointer whitespace-nowrap">
                    更多...
                  </button>
                )}
              </div>
            </section>
          )}

          {/* Related Movies */}
          {relatedMovies.length > 0 && (
            <section className="mt-10">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                {relatedMovies.map((m) => (
                  <Link
                    key={m.id}
                    to={`/detail/${m.id}`}
                    className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={m.poster}
                        alt={m.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 right-2 bg-primary-500 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                        {m.rating}
                      </div>
                    </div>
                    <div className="p-2">
                      <h3 className="text-xs font-heading font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                        {m.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{m.year}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </div>
  );
}